import api from '../api/axios';

const API_BASE = '/api/chat';

// In-memory cache for instant responses
let unreadCountCache = {
  total: 0,
  conversations: {},
  lastUpdated: null
};

export const chatUnreadAPI = {
  // Get total unread count with caching
  getUnreadCount: async () => {
    try {
      // Return cached value immediately if fresh (less than 5 seconds old)
      const now = Date.now();
      if (unreadCountCache.lastUpdated && (now - unreadCountCache.lastUpdated) < 5000) {
        return {
          data: {
            unreadCount: unreadCountCache.total
          }
        };
      }

      const response = await api.get(`${API_BASE}/unread-count`);
      
      // Update cache
      if (response.data?.data?.unreadCount !== undefined) {
        unreadCountCache.total = response.data.data.unreadCount;
        unreadCountCache.lastUpdated = now;
      }
      
      return response;
    } catch (error) {
      console.error('Error fetching unread count:', error);
      // Return cached value on error
      if (unreadCountCache.lastUpdated) {
        return {
          data: {
            unreadCount: unreadCountCache.total
          }
        };
      }
      throw error;
    }
  },

  // Get unread count per conversation with caching
  getUnreadConversations: async () => {
    try {
      const response = await api.get(`${API_BASE}/unread-conversations`);
      
      // Update conversation cache
      if (response.data?.data?.unreadConversations) {
        response.data.data.unreadConversations.forEach(conv => {
          unreadCountCache.conversations[conv._id] = conv.unreadCount;
        });
        unreadCountCache.lastUpdated = Date.now();
      }
      
      return response;
    } catch (error) {
      console.error('Error fetching unread conversations:', error);
      throw error;
    }
  },

  // Mark message as read with instant cache update
  markMessageAsRead: async (messageId) => {
    try {
      const response = await api.put(`${API_BASE}/messages/${messageId}/read`);
      
      // Instantly update cache (optimistic update)
      if (unreadCountCache.total > 0) {
        unreadCountCache.total = Math.max(0, unreadCountCache.total - 1);
        unreadCountCache.lastUpdated = Date.now();
      }
      
      return response;
    } catch (error) {
      console.error('Error marking message as read:', error);
      throw error;
    }
  },

  // Mark all messages in conversation as read with instant cache update
  markAllAsRead: async (userId) => {
    try {
      const response = await api.put(`${API_BASE}/${userId}/read-all`);
      
      // Create conversation ID for cache update
      const conversationId = [userId, 'currentUser'].sort().join('_');
      const conversationUnreadCount = unreadCountCache.conversations[conversationId] || 0;
      
      // Instantly update cache (optimistic update)
      unreadCountCache.total = Math.max(0, unreadCountCache.total - conversationUnreadCount);
      unreadCountCache.conversations[conversationId] = 0;
      unreadCountCache.lastUpdated = Date.now();
      
      return response;
    } catch (error) {
      console.error('Error marking all messages as read:', error);
      throw error;
    }
  },

  // Real-time cache updates (called by socket events)
  updateUnreadCountCache: (newCount, conversationId = null, increment = false) => {
    const now = Date.now();
    
    if (increment) {
      unreadCountCache.total = Math.max(0, unreadCountCache.total + (newCount || 1));
    } else {
      unreadCountCache.total = Math.max(0, newCount || 0);
    }
    
    if (conversationId) {
      if (increment) {
        unreadCountCache.conversations[conversationId] = 
          (unreadCountCache.conversations[conversationId] || 0) + (newCount || 1);
      } else {
        unreadCountCache.conversations[conversationId] = Math.max(0, newCount || 0);
      }
    }
    
    unreadCountCache.lastUpdated = now;
    
    return unreadCountCache.total;
  },

  // Get cached unread count (instant)
  getCachedUnreadCount: () => {
    return unreadCountCache.total;
  },

  // Clear cache
  clearCache: () => {
    unreadCountCache = {
      total: 0,
      conversations: {},
      lastUpdated: null
    };
  }
};
